import React, { useState, useRef, useEffect } from 'react';
import { Search, Plus, Pencil, Trash2, CheckCircle, ArrowRight, ChevronDown, AlertTriangle, ArrowLeftRight } from 'lucide-react';

const SEGMENTO_OPTIONS = [
    'Residência',
    'Mercado / Supermercado',
    'Padaria / Confeitaria',
    'Restaurante / Lanchonete',
    'Sorveteria',
    'Farmácia / Drogaria',
    'Academia / Esporte',
    'Salão de Beleza / Barbearia',
    'Posto de Combustível',
    'Escritório / Consultório',
    'Clínica / Hospital',
    'Escola / Colégio',
    'Hotel / Pousada',
    'Loja de Roupas / Moda',
    'Pet Shop',
    'Atacadista / Distribuidor',
    'Indústria',
    'Condomínio',
    'Comércio em Geral',
    'Outro',
];
import { supabase } from '../../../../src/lib/supabase';
import { buscarUcComMesmoNumero, descreverUcExistente, numeroRepetidoNoLote } from '../../../../src/lib/ucDuplicada';
import type { UcExistente } from '../../../../src/lib/ucDuplicada';
import { AvisoUcExistente } from '../../../../src/components/TrocaDonoAviso';
import { validarJustificativaTroca } from '../../../../src/lib/trocaDonoUc';
import { normalizeCpfCnpj } from '../../../../src/lib/utils';
import { useToast } from '../../../../src/contexts/ToastContext';

export interface UCData {
    id: string; // Temp ID
    numero_uc: string;
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string;
    quadra: string;
    lote: string;
    bairro: string;
    cidade: string;
    estado: string;
    consumo_medio_kwh?: number;
    tipo_ligacao?: 'Monofásico' | 'Bifásico' | 'Trifásico';
    grupo_tarifario?: 'A1' | 'A2' | 'A3' | 'A3a' | 'A4' | 'AS' | 'B1' | 'B2' | 'B3' | 'B4';
    concessionaria?: string;
    valor_tarifa?: number;
    titular_fatura?: string;
    cpf_cnpj_titular?: string;
    data_nascimento_titular?: string;
    desconto_tarifa?: number;
    desconto_bandeira?: number;
    carencia?: string;
    observacoes_uc?: string;
    num_medidor?: string;
    outro_numero_uc?: string;
    segmento?: string;
    segmento_outro?: string;
    /** UC que já existe e muda de dono (venda do estabelecimento): não é inserida,
     *  vira pedido de troca quando a negociação é criada. */
    troca_dono?: {
        uc_id: number;
        justificativa: string;
        cliente_atual: string | null;
    };
}

interface StepUCProps {
    ucs: UCData[];
    onAddUC: (uc: UCData) => void;
    onRemoveUC: (id: string) => void;
    onBack: () => void;
    onFinish: () => void;
    loading: boolean;
    wantPlan: boolean;
    setWantPlan: (value: boolean) => void;
    hasNextStep: boolean;
    cliente_nome?: string;
    cliente_cpf_cnpj?: string;
    initialUC?: Partial<UCData>;
    /** Mostrado em destaque no topo — usado quando uma UC não pôde ser salva (dado inválido) e o formulário reabre com os dados dela para correção. */
    errorMessage?: string;
    /** Tipo de cliente da negociação — em PJ escondemos datas de nascimento e mostramos o bloco Representante. */
    tipoCliente?: 'PF' | 'PJ';
    /** Representante da associação (PJ) — nível negociação. */
    representante?: { nome: string; cpf: string; data_nascimento: string };
    onChangeRepresentante?: (field: 'nome' | 'cpf' | 'data_nascimento', value: string) => void;
}

const StepUC: React.FC<StepUCProps> = ({ ucs, onAddUC, onRemoveUC, onBack, onFinish, loading, wantPlan, setWantPlan, hasNextStep, cliente_nome, cliente_cpf_cnpj, initialUC, errorMessage, tipoCliente, representante, onChangeRepresentante }) => {
    const { addToast } = useToast();
    const isPJ = tipoCliente === 'PJ';
    // PJ: o representante é o titular da fatura (padrão) ou outra pessoa.
    const [repIsTitular, setRepIsTitular] = useState(true);

    const maskCPFLocal = (value: string) => value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    const [currentUC, setCurrentUC] = useState<UCData>({
        id: Math.random().toString(36).substr(2, 9),
        numero_uc: '',
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        quadra: '',
        lote: '',
        bairro: '',
        cidade: '',
        estado: '',
        consumo_medio_kwh: undefined,
        valor_tarifa: undefined,
        tipo_ligacao: undefined,
        grupo_tarifario: undefined,
        concessionaria: undefined,
        titular_fatura: '',
        cpf_cnpj_titular: '',
        data_nascimento_titular: '',
        desconto_tarifa: 20,
        desconto_bandeira: 50,
        carencia: 'sem carência',
        observacoes_uc: '',
        num_medidor: '',
        outro_numero_uc: '',
        ...initialUC
    });

    const [searching, setSearching] = useState(false);
    // UC digitada que já existe no sistema — abre o aviso com a troca de dono.
    const [ucExistente, setUcExistente] = useState<UcExistente | null>(null);
    const [pedindoTroca, setPedindoTroca] = useState(false);
    const [justificativaTroca, setJustificativaTroca] = useState('');
    const avisoRef = useRef<HTMLDivElement>(null);
    const [valorTarifaInput, setValorTarifaInput] = useState(initialUC?.valor_tarifa != null ? String(initialUC.valor_tarifa).replace('.', ',') : '');
    const [consumoInput, setConsumoInput] = useState(initialUC?.consumo_medio_kwh != null ? String(initialUC.consumo_medio_kwh).replace('.', ',') : '');
    const [sameNameAsClient, setSameNameAsClient] = useState(false);
    const [sameCpfAsClient, setSameCpfAsClient] = useState(false);

    // Segmento combobox
    const [segmentoSearch, setSegmentoSearch] = useState('');
    const [segmentoOpen, setSegmentoOpen] = useState(false);
    const segmentoRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (segmentoRef.current && !segmentoRef.current.contains(e.target as Node)) {
                setSegmentoOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filteredSegmentos = SEGMENTO_OPTIONS.filter(s =>
        s.toLowerCase().includes(segmentoSearch.toLowerCase())
    );

    // "Sem" checkboxes
    const [semNumero, setSemNumero] = useState(false);
    const [semQuadra, setSemQuadra] = useState(false);
    const [semLote, setSemLote] = useState(false);

    // Destaque e bloqueio caso o usuário preencha o formulário e não adicione
    const isFormPartiallyFilled = currentUC.numero_uc.trim() !== '' || currentUC.numero.trim() !== '' || currentUC.cep.trim() !== '';

    // Item #5: botão-mestre — preenche o titular da fatura com os dados do cliente da negociação.
    const hasClientData = !!(cliente_nome?.trim() || cliente_cpf_cnpj?.trim());
    const fillTitularFromClient = () => {
        const nome = cliente_nome || '';
        const cpf = cliente_cpf_cnpj ? cliente_cpf_cnpj.replace(/\D/g, '') : '';
        setCurrentUC(prev => ({ ...prev, titular_fatura: nome || prev.titular_fatura, cpf_cnpj_titular: cpf || prev.cpf_cnpj_titular }));
        if (nome) setSameNameAsClient(true);
        if (cpf) setSameCpfAsClient(true);
        // PJ com representante = titular: propaga para o representante da associação
        if (isPJ && repIsTitular && onChangeRepresentante) {
            if (nome) onChangeRepresentante('nome', nome);
            if (cpf) onChangeRepresentante('cpf', cpf);
        }
    };

    // PJ: mantém o representante em sincronia com o titular da fatura quando "É o titular" está ativo.
    const syncRepFromTitular = (nome?: string, cpf?: string) => {
        if (!(isPJ && repIsTitular && onChangeRepresentante)) return;
        if (nome !== undefined) onChangeRepresentante('nome', nome);
        if (cpf !== undefined) onChangeRepresentante('cpf', cpf);
    };

    const handleSearchCEP = async () => {
        const cep = currentUC.cep.replace(/\D/g, '');
        if (cep.length !== 8) return;

        setSearching(true);
        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
            if (!response.ok) throw new Error('CEP não encontrado');

            const data = await response.json();
            setCurrentUC(prev => ({
                ...prev,
                logradouro: data.street || '',
                bairro: data.neighborhood || '',
                cidade: data.city || '',
                estado: data.state || ''
            }));
        } catch (error) {
            console.error('Error fetching CEP:', error);
        } finally {
            setSearching(false);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedUC = currentUC.numero_uc.trim();

        // Bloqueia duplicata dentro da própria lista local. O INSERT em lote no wizard
        // é atômico: uma UC repetida derruba TODAS. A comparação cruza "Número da UC" e
        // "Outro Nº UC" sem máscara — '100.151.067-54' e '10015106754' são a mesma UC.
        if (trimmedUC && numeroRepetidoNoLote([...ucs, currentUC])) {
            addToast(`A UC ${trimmedUC} já foi adicionada a esta negociação.`, 'error');
            return;
        }

        // UC já cadastrada no sistema (em qualquer das duas colunas, com ou sem máscara).
        // Vem antes do segmento: se a UC existe, o caminho é a troca de dono, que
        // mantém o cadastro dela e não pede o resto do formulário.
        if (trimmedUC) {
            try {
                const existente = await buscarUcComMesmoNumero(supabase, trimmedUC, currentUC.outro_numero_uc);
                if (existente) {
                    setUcExistente(existente);
                    setPedindoTroca(false);
                    setJustificativaTroca('');
                    addToast(`${descreverUcExistente(existente)} Se o estabelecimento foi vendido, peça a troca de dono no aviso do formulário.`, 'error');
                    avisoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    return;
                }
            } catch (err) {
                // A trigger trg_bloqueia_uc_duplicada barra na gravação mesmo se esta checagem falhar.
                console.error('Error checking UC uniqueness:', err);
            }
        }

        if (!currentUC.segmento) {
            addToast('O campo Segmento é obrigatório.', 'error');
            return;
        }

        if (currentUC.segmento === 'Outro' && !currentUC.segmento_outro?.trim()) {
            addToast('Por favor, descreva o segmento no campo "Qual segmento?".', 'error');
            return;
        }

        // Resolve the final segmento value before adding
        const segmentoFinal = currentUC.segmento === 'Outro'
            ? (currentUC.segmento_outro?.trim() || 'Outro')
            : currentUC.segmento;

        onAddUC({ ...currentUC, segmento: segmentoFinal });
        limparFormulario();
    };

    const limparFormulario = () => {
        setCurrentUC({
            id: Math.random().toString(36).substr(2, 9),
            numero_uc: '',
            tipo_ligacao: '' as any,
            grupo_tarifario: '' as any,
            concessionaria: 'Equatorial Goiás',
            desconto_tarifa: 20,
            desconto_bandeira: 50,
            carencia: 'sem carência',
            consumo_medio_kwh: undefined,
            titular_fatura: '',
            cpf_cnpj_titular: '',
            data_nascimento_titular: '',
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            quadra: '',
            lote: '',
            bairro: '',
            cidade: '',
            estado: '',
            observacoes_uc: '',
            num_medidor: '',
            outro_numero_uc: '',
            segmento: undefined,
            segmento_outro: undefined
        });
        setValorTarifaInput('');
        setConsumoInput('');
        setSameNameAsClient(false);
        setSameCpfAsClient(false);
        setSemNumero(false);
        setSemQuadra(false);
        setSemLote(false);
        setSegmentoSearch('');
        setSegmentoOpen(false);
        setUcExistente(null);
        setPedindoTroca(false);
        setJustificativaTroca('');
    };

    // Venda do estabelecimento: a UC existente entra na lista como pedido de troca
    // de dono. O cadastro dela (endereço, titular, dados técnicos) fica como está.
    const adicionarComoTroca = () => {
        if (!ucExistente) return;
        const erro = validarJustificativaTroca(justificativaTroca);
        if (erro) {
            addToast(erro, 'error');
            return;
        }
        onAddUC({
            id: Math.random().toString(36).substr(2, 9),
            numero_uc: ucExistente.numero_uc,
            outro_numero_uc: ucExistente.outro_numero_uc || '',
            cep: '',
            logradouro: '',
            numero: '',
            complemento: '',
            quadra: '',
            lote: '',
            bairro: '',
            cidade: '',
            estado: '',
            consumo_medio_kwh: ucExistente.consumo_medio_kwh ?? undefined,
            troca_dono: {
                uc_id: ucExistente.id,
                justificativa: justificativaTroca.trim(),
                cliente_atual: ucExistente.nome_cliente,
            },
        });
        limparFormulario();
    };

    // Recarrega a UC no formulário para correção e a tira da lista — reenviar (handleAdd)
    // a devolve com o mesmo id, já que a lista ainda não foi persistida neste passo do wizard.
    const handleEditUC = (uc: UCData) => {
        setCurrentUC({ ...uc });
        setValorTarifaInput(uc.valor_tarifa != null ? String(uc.valor_tarifa).replace('.', ',') : '');
        setConsumoInput(uc.consumo_medio_kwh != null ? String(uc.consumo_medio_kwh).replace('.', ',') : '');
        setSemNumero(uc.numero === 'S/N');
        setSemQuadra(uc.quadra === 'Sem quadra');
        setSemLote(uc.lote === 'Sem lote');
        onRemoveUC(uc.id);
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const inputClass = "w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500";
    const labelClass = "block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1";
    const disabledInputClass = "w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 cursor-not-allowed";

    return (
        <div className="flex flex-col gap-4 animate-fade-in max-h-[70vh] overflow-y-auto custom-scrollbar px-1">
            <div className="text-center mb-2">
                <h3 className="text-lg font-bold text-gray-800 dark:text-slate-100">Unidades Consumidoras</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400">Adicione as UCs vinculadas a este cliente.</p>
            </div>

            {errorMessage && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl text-sm border border-red-200 dark:border-red-800/40">
                    <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                    <div>
                        <p className="font-bold">Não foi possível salvar esta UC</p>
                        <p>{errorMessage} Revise os dados abaixo e clique em "Adicionar esta UC" novamente.</p>
                    </div>
                </div>
            )}

            {/* List of Added UCs */}
            {ucs.length > 0 && (
                <div className="flex flex-col gap-2 mb-4">
                    {ucs.map(uc => (
                        uc.troca_dono ? (
                        <div key={uc.id} className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg">
                            <div>
                                <p className="text-sm font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                                    UC: {uc.numero_uc}
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
                                        <ArrowLeftRight size={10} /> Troca de dono
                                    </span>
                                </p>
                                <p className="text-xs text-gray-700 dark:text-slate-300">Hoje de: {uc.troca_dono.cliente_atual || '—'} · cadastro da UC mantido</p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 italic">"{uc.troca_dono.justificativa}"</p>
                            </div>
                            <button
                                onClick={() => onRemoveUC(uc.id)}
                                title="Remover esta UC"
                                className="p-2 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors shrink-0"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                        ) : (
                        <div key={uc.id} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 rounded-lg">
                            <div>
                                <p className="text-sm font-bold text-gray-800 dark:text-slate-100">UC: {uc.numero_uc}</p>
                                {uc.titular_fatura && (
                                    <p className="text-xs text-gray-700 dark:text-slate-300 font-semibold mb-1">Titular: {uc.titular_fatura} {uc.cpf_cnpj_titular ? `(${normalizeCpfCnpj(uc.cpf_cnpj_titular)})` : ''}</p>
                                )}
                                <p className="text-xs text-gray-500 dark:text-slate-400">{uc.logradouro}, {uc.numero || 'S/N'} - {uc.cidade}/{uc.estado}</p>
                                {uc.consumo_medio_kwh && (
                                    <p className="text-xs text-gray-600 dark:text-slate-400 mt-1">
                                        Média: <span className="font-semibold">{uc.consumo_medio_kwh} kWh</span>
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    onClick={() => handleEditUC(uc)}
                                    title="Editar esta UC"
                                    className="p-2 text-gray-400 dark:text-slate-500 hover:text-primary hover:bg-primary/10 rounded-full transition-colors"
                                >
                                    <Pencil size={16} />
                                </button>
                                <button
                                    onClick={() => onRemoveUC(uc.id)}
                                    title="Remover esta UC"
                                    className="p-2 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                        )
                    ))}
                </div>
            )}

            {/* Form to Add UC */}
            <form ref={formRef} onSubmit={handleAdd} className="bg-gray-50 dark:bg-slate-800/50 p-4 rounded-xl border border-gray-200 dark:border-slate-700/50">

                {/* Segmento (obrigatório) */}
                <div className="mb-4">
                    <label className={labelClass}>
                        Segmento <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={segmentoRef}>
                        <div
                            className={`flex items-center w-full border rounded-lg px-3 py-2 text-sm cursor-pointer bg-white dark:bg-slate-900 ${!currentUC.segmento ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-slate-700'} focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20`}
                            onClick={() => setSegmentoOpen(v => !v)}
                        >
                            <input
                                type="text"
                                value={segmentoOpen ? segmentoSearch : (currentUC.segmento === 'Outro' ? `Outro: ${currentUC.segmento_outro || ''}` : (currentUC.segmento || ''))}
                                onChange={e => {
                                    setSegmentoSearch(e.target.value);
                                    setSegmentoOpen(true);
                                }}
                                onFocus={() => {
                                    setSegmentoSearch('');
                                    setSegmentoOpen(true);
                                }}
                                placeholder="Digite para buscar o segmento..."
                                className="flex-1 bg-transparent outline-none text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                                readOnly={!segmentoOpen}
                            />
                            <ChevronDown size={14} className={`text-gray-400 transition-transform ${segmentoOpen ? 'rotate-180' : ''}`} />
                        </div>
                        {segmentoOpen && (
                            <ul className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg max-h-52 overflow-y-auto custom-scrollbar">
                                {filteredSegmentos.length === 0 ? (
                                    <li className="px-3 py-2 text-sm text-gray-400 dark:text-slate-500">Nenhum resultado encontrado</li>
                                ) : filteredSegmentos.map(opt => (
                                    <li
                                        key={opt}
                                        onClick={() => {
                                            setCurrentUC(prev => ({ ...prev, segmento: opt, segmento_outro: opt === 'Outro' ? prev.segmento_outro : undefined }));
                                            setSegmentoSearch('');
                                            setSegmentoOpen(false);
                                        }}
                                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors ${currentUC.segmento === opt ? 'bg-primary/10 dark:bg-primary/20 font-semibold text-primary' : 'text-gray-800 dark:text-slate-100'}`}
                                    >
                                        {opt}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {/* Campo "Qual segmento?" quando Outro é selecionado */}
                    {currentUC.segmento === 'Outro' && (
                        <input
                            type="text"
                            value={currentUC.segmento_outro || ''}
                            onChange={e => setCurrentUC(prev => ({ ...prev, segmento_outro: e.target.value }))}
                            className={`${inputClass} mt-2`}
                            placeholder="Qual é o segmento? Ex: Lavanderia, Farmácia de Manipulação..."
                            required
                        />
                    )}
                </div>

                {/* Row 1: UC Number, Outro Nº UC, Nº Medidor, Concessionária, Consumo */}
                <div className="grid grid-cols-5 gap-3 mb-4">
                    <div>
                        <label className={labelClass}>
                            Número da UC <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={currentUC.numero_uc}
                            onChange={(e) => { setCurrentUC({ ...currentUC, numero_uc: e.target.value }); setUcExistente(null); }}
                            className={inputClass}
                            placeholder="12345678"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Outro Nº UC</label>
                        <input
                            type="text"
                            value={currentUC.outro_numero_uc || ''}
                            onChange={(e) => { setCurrentUC({ ...currentUC, outro_numero_uc: e.target.value }); setUcExistente(null); }}
                            className={inputClass}
                            placeholder="Ex: 12345678"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Nº do Medidor</label>
                        <input
                            type="text"
                            value={currentUC.num_medidor || ''}
                            onChange={(e) => setCurrentUC({ ...currentUC, num_medidor: e.target.value })}
                            className={inputClass}
                            placeholder="Ex: 00123456"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Concessionária</label>
                        <select
                            value={currentUC.concessionaria || ''}
                            onChange={e => setCurrentUC({ ...currentUC, concessionaria: e.target.value })}
                            className={inputClass}
                        >
                            <option value="">Selecione...</option>
                            <option value="Equatorial Goiás">Equatorial Goiás</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Consumo Médio (kWh)</label>
                        <input
                            type="text"
                            value={consumoInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val.includes('.')) return;
                                if (/^[0-9]*[,]?[0-9]*$/.test(val)) {
                                    setConsumoInput(val);
                                }
                            }}
                            onBlur={() => {
                                if (consumoInput) {
                                    const numVal = parseFloat(consumoInput.replace(',', '.'));
                                    setCurrentUC(prev => ({ ...prev, consumo_medio_kwh: isNaN(numVal) ? undefined : numVal }));
                                } else {
                                    setCurrentUC(prev => ({ ...prev, consumo_medio_kwh: undefined }));
                                }
                            }}
                            className={inputClass}
                            placeholder="Ex: 350,50"
                        />
                    </div>
                </div>

                <div ref={avisoRef}>
                    {ucExistente && (
                        <AvisoUcExistente
                            uc={ucExistente}
                            pedindo={pedindoTroca}
                            justificativa={justificativaTroca}
                            onPedir={() => setPedindoTroca(true)}
                            onJustificativa={setJustificativaTroca}
                            onConfirmar={adicionarComoTroca}
                            onCancelar={() => { setPedindoTroca(false); setJustificativaTroca(''); }}
                        />
                    )}
                </div>

                {/* Row 2: Valor Tarifa, Tipo Ligação, Grupo Tarifário */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div>
                        <label className={labelClass}>Valor Tarifa (R$)</label>
                        <input
                            type="text"
                            value={valorTarifaInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^[0-9]*[.,]?[0-9]*$/.test(val)) {
                                    setValorTarifaInput(val);
                                }
                            }}
                            onBlur={() => {
                                if (valorTarifaInput) {
                                    const numVal = parseFloat(valorTarifaInput.replace(',', '.'));
                                    setCurrentUC(prev => ({ ...prev, valor_tarifa: isNaN(numVal) ? undefined : numVal }));
                                } else {
                                    setCurrentUC(prev => ({ ...prev, valor_tarifa: undefined }));
                                }
                            }}
                            className={inputClass}
                            placeholder="1,125214"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Tipo de Ligação</label>
                        <select
                            value={currentUC.tipo_ligacao || ''}
                            onChange={e => setCurrentUC({ ...currentUC, tipo_ligacao: e.target.value as any })}
                            className={inputClass}
                        >
                            <option value="">Selecione...</option>
                            <option value="Monofásico">Monofásico</option>
                            <option value="Bifásico">Bifásico</option>
                            <option value="Trifásico">Trifásico</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Grupo Tarifário</label>
                        <select
                            value={currentUC.grupo_tarifario || ''}
                            onChange={e => setCurrentUC({ ...currentUC, grupo_tarifario: e.target.value as any })}
                            className={inputClass}
                        >
                            <option value="">Selecione...</option>
                            <optgroup label="Grupo A - Alta Tensão">
                                <option value="A1">A1 (Tensão ≥ 230 kV)</option>
                                <option value="A2">A2 (88 kV a 138 kV)</option>
                                <option value="A3">A3 (69 kV)</option>
                                <option value="A3a">A3a (30 kV a 44 kV)</option>
                                <option value="A4">A4 (2,3 kV a 25 kV)</option>
                                <option value="AS">AS (Subterrâneo, &lt; 2,3 kV)</option>
                            </optgroup>
                            <optgroup label="Grupo B - Baixa Tensão">
                                <option value="B1">B1 (Residencial)</option>
                                <option value="B2">B2 (Rural)</option>
                                <option value="B3">B3 (Demais Classes)</option>
                                <option value="B4">B4 (Iluminação Pública)</option>
                            </optgroup>
                        </select>
                    </div>
                </div>

                {/* Row 3: Titular Fields */}
                <div className="border-t border-gray-100 dark:border-slate-700/50 pt-4 mb-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <label className={labelClass.replace(' mb-1', '')}>Dados do Titular da Fatura</label>
                        {hasClientData && (
                            <button
                                type="button"
                                onClick={fillTitularFromClient}
                                className="flex items-center gap-1 text-[11px] font-bold text-primary bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30 px-2 py-1 rounded-lg transition-colors whitespace-nowrap"
                            >
                                <CheckCircle size={12} /> Preencher com dados do cliente
                            </button>
                        )}
                    </div>
                    <div className={`grid grid-cols-1 ${isPJ ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-3`}>
                        <div>
                            <div className="flex items-center gap-2 mb-1 justify-between">
                                <label className={labelClass.replace(' mb-1', '')}>
                                    Nome do Titular da Fatura
                                </label>
                                <label className="flex items-center gap-1 text-[10px] text-primary cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10 px-1 py-0.5 rounded transition-colors break-keep whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={sameNameAsClient}
                                        onChange={(e) => {
                                            setSameNameAsClient(e.target.checked);
                                            if (e.target.checked && cliente_nome) {
                                                setCurrentUC(prev => ({ ...prev, titular_fatura: cliente_nome }));
                                                syncRepFromTitular(cliente_nome, undefined);
                                            }
                                        }}
                                        className="rounded border-gray-300 text-primary focus:ring-primary w-3 h-3"
                                    />
                                    O mesmo do cliente
                                </label>
                            </div>
                            <input
                                type="text"
                                value={currentUC.titular_fatura || ''}
                                onChange={(e) => {
                                    setCurrentUC({ ...currentUC, titular_fatura: e.target.value });
                                    if (sameNameAsClient) setSameNameAsClient(false);
                                    syncRepFromTitular(e.target.value, undefined);
                                }}
                                className={inputClass}
                                placeholder="Nome Completo do Titular"
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1 justify-between">
                                <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-tighter sm:tracking-normal">
                                    CPF/CNPJ do Titular
                                </label>
                                <label className="flex items-center gap-1 text-[10px] text-primary cursor-pointer hover:bg-primary/5 dark:hover:bg-primary/10 px-1 py-0.5 rounded transition-colors break-keep whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={sameCpfAsClient}
                                        onChange={(e) => {
                                            setSameCpfAsClient(e.target.checked);
                                            if (e.target.checked && cliente_cpf_cnpj) {
                                                const formattedCpfCnpj = cliente_cpf_cnpj.replace(/\D/g, '');
                                                setCurrentUC(prev => ({ ...prev, cpf_cnpj_titular: formattedCpfCnpj }));
                                                syncRepFromTitular(undefined, formattedCpfCnpj);
                                            }
                                        }}
                                        className="rounded border-gray-300 text-primary focus:ring-primary w-3 h-3"
                                    />
                                    O mesmo do cliente
                                </label>
                            </div>
                            <input
                                type="text"
                                value={currentUC.cpf_cnpj_titular || ''}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setCurrentUC({ ...currentUC, cpf_cnpj_titular: digits });
                                    if (sameCpfAsClient) setSameCpfAsClient(false);
                                    syncRepFromTitular(undefined, digits);
                                }}
                                className={inputClass}
                                placeholder="Apenas números"
                            />
                        </div>
                        {/* Item #6: PF mantém data de nascimento do titular; PJ não precisa (usa a do representante) */}
                        {!isPJ && (
                            <div>
                                <label className={labelClass}>
                                    Data de Nascimento do Titular
                                </label>
                                <input
                                    type="date"
                                    value={currentUC.data_nascimento_titular || ''}
                                    onChange={(e) => setCurrentUC({ ...currentUC, data_nascimento_titular: e.target.value })}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-[7px] text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Item #6: Representante da Associação (somente PJ) */}
                {isPJ && (
                    <div className="mb-4 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <label className={labelClass.replace(' mb-1', '')}>Representante da Associação</label>
                            <div className="inline-flex rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden text-[11px]">
                                <button
                                    type="button"
                                    onClick={() => { setRepIsTitular(true); onChangeRepresentante?.('nome', currentUC.titular_fatura || ''); onChangeRepresentante?.('cpf', currentUC.cpf_cnpj_titular || ''); }}
                                    className={repIsTitular ? 'px-3 py-1 font-bold bg-primary text-white' : 'px-3 py-1 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'}
                                >
                                    É o titular da fatura
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRepIsTitular(false)}
                                    className={!repIsTitular ? 'px-3 py-1 font-bold bg-primary text-white' : 'px-3 py-1 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'}
                                >
                                    Outra pessoa
                                </button>
                            </div>
                        </div>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mb-3">
                            Pessoa física que assina o termo em nome da associação (vai para o termo e para a análise de crédito).{' '}
                            {repIsTitular ? 'Usando o nome e o CPF do titular da fatura acima.' : 'Informe os dados do representante.'}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <label className={labelClass}>Nome do Representante</label>
                                <input
                                    type="text"
                                    value={representante?.nome || ''}
                                    disabled={repIsTitular}
                                    onChange={(e) => onChangeRepresentante?.('nome', e.target.value)}
                                    className={repIsTitular ? disabledInputClass : inputClass}
                                    placeholder="Nome completo"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>CPF do Representante</label>
                                <input
                                    type="text"
                                    value={representante?.cpf ? maskCPFLocal(representante.cpf) : ''}
                                    disabled={repIsTitular}
                                    onChange={(e) => onChangeRepresentante?.('cpf', e.target.value.replace(/\D/g, ''))}
                                    className={repIsTitular ? disabledInputClass : inputClass}
                                    placeholder="000.000.000-00"
                                    maxLength={14}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Data de Nascimento</label>
                                <input
                                    type="date"
                                    value={representante?.data_nascimento || ''}
                                    onChange={(e) => onChangeRepresentante?.('data_nascimento', e.target.value)}
                                    className="w-full border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-[7px] text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Row 4: Address - CEP + Logradouro */}
                <div className="grid grid-cols-3 gap-3 mb-3 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                    <div className="col-span-1">
                        <label className={labelClass}>CEP</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={currentUC.cep}
                                onChange={(e) => setCurrentUC({ ...currentUC, cep: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                                onBlur={handleSearchCEP}
                                className={inputClass}
                                placeholder="00000000"
                            />
                            {searching && (
                                <div className="absolute right-3 top-2.5 animate-spin">
                                    <Search size={14} className="text-primary" />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>Logradouro</label>
                        <input
                            type="text"
                            value={currentUC.logradouro}
                            onChange={(e) => setCurrentUC({ ...currentUC, logradouro: e.target.value })}
                            className={inputClass}
                            placeholder="Rua, Avenida..."
                        />
                    </div>
                </div>

                {/* Row 5: Número + Quadra + Lote (with "Sem" checkboxes) */}
                <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase">
                                Número
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semNumero}
                                    onChange={(e) => {
                                        setSemNumero(e.target.checked);
                                        if (e.target.checked) {
                                            setCurrentUC(prev => ({ ...prev, numero: 'S/N' }));
                                        } else {
                                            setCurrentUC(prev => ({ ...prev, numero: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 text-primary focus:ring-primary w-3 h-3"
                                />
                                S/N
                            </label>
                        </div>
                        <input
                            type="text"
                            value={currentUC.numero}
                            onChange={(e) => setCurrentUC({ ...currentUC, numero: e.target.value })}
                            disabled={semNumero}
                            className={semNumero ? disabledInputClass : inputClass}
                            placeholder="123"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase">
                                Quadra
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semQuadra}
                                    onChange={(e) => {
                                        setSemQuadra(e.target.checked);
                                        if (e.target.checked) {
                                            setCurrentUC(prev => ({ ...prev, quadra: 'Sem quadra' }));
                                        } else {
                                            setCurrentUC(prev => ({ ...prev, quadra: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 text-primary focus:ring-primary w-3 h-3"
                                />
                                Não existe
                            </label>
                        </div>
                        <input
                            type="text"
                            value={currentUC.quadra}
                            onChange={(e) => setCurrentUC({ ...currentUC, quadra: e.target.value })}
                            disabled={semQuadra}
                            className={semQuadra ? disabledInputClass : inputClass}
                            placeholder="Qd. 10"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase">
                                Lote
                            </label>
                            <label className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-slate-400 cursor-pointer hover:text-primary transition-colors whitespace-nowrap">
                                <input
                                    type="checkbox"
                                    checked={semLote}
                                    onChange={(e) => {
                                        setSemLote(e.target.checked);
                                        if (e.target.checked) {
                                            setCurrentUC(prev => ({ ...prev, lote: 'Sem lote' }));
                                        } else {
                                            setCurrentUC(prev => ({ ...prev, lote: '' }));
                                        }
                                    }}
                                    className="rounded border-gray-300 text-primary focus:ring-primary w-3 h-3"
                                />
                                Não existe
                            </label>
                        </div>
                        <input
                            type="text"
                            value={currentUC.lote}
                            onChange={(e) => setCurrentUC({ ...currentUC, lote: e.target.value })}
                            disabled={semLote}
                            className={semLote ? disabledInputClass : inputClass}
                            placeholder="Lt. 05"
                        />
                    </div>
                </div>

                {/* Row 6: Compl + Bairro */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className={labelClass}>Compl.</label>
                        <input
                            type="text"
                            value={currentUC.complemento}
                            onChange={(e) => setCurrentUC({ ...currentUC, complemento: e.target.value })}
                            className={inputClass}
                            placeholder="Ap 101"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Bairro</label>
                        <input
                            type="text"
                            value={currentUC.bairro}
                            onChange={(e) => setCurrentUC({ ...currentUC, bairro: e.target.value })}
                            className={inputClass}
                            placeholder="Centro"
                        />
                    </div>
                </div>

                {/* Row 7: Cidade + Estado */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className={labelClass}>Cidade</label>
                        <input
                            type="text"
                            value={currentUC.cidade}
                            onChange={(e) => setCurrentUC({ ...currentUC, cidade: e.target.value })}
                            className={inputClass}
                            placeholder="Cidade"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Estado</label>
                        <input
                            type="text"
                            value={currentUC.estado}
                            onChange={(e) => setCurrentUC({ ...currentUC, estado: e.target.value })}
                            className={inputClass}
                            placeholder="UF"
                        />
                    </div>
                </div>

                {/* Row 8: Observações */}
                <div className="mb-4 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                    <label className={labelClass}>Observações</label>
                    <textarea
                        value={currentUC.observacoes_uc || ''}
                        onChange={e => setCurrentUC({ ...currentUC, observacoes_uc: e.target.value })}
                        rows={2}
                        className={`${inputClass} resize-y`}
                        placeholder="Observações sobre esta UC (será salvo como nota na negociação)..."
                    />
                </div>

                <div className="flex justify-end mt-2">
                    <button
                        type="submit"
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 ${isFormPartiallyFilled
                            ? 'bg-primary text-white shadow-lg shadow-primary/40 animate-pulse ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                            : 'text-primary bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30'
                            }`}
                    >
                        <Plus size={16} /> Adicionar esta UC
                    </button>
                </div>
            </form>

            {/* Plan Selection Toggle */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-700/50">
                <div className="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in">
                    <input
                        type="checkbox"
                        checked={wantPlan}
                        onChange={(e) => setWantPlan(e.target.checked)}
                        className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer translate-x-1 top-1 transition-transform checked:translate-x-5 checked:border-primary"
                        style={{ right: 'unset' }}
                    />
                    <div onClick={() => setWantPlan(!wantPlan)} className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors ${wantPlan ? 'bg-primary' : 'bg-gray-300 dark:bg-slate-600'}`}></div>
                </div>
                <label onClick={() => setWantPlan(!wantPlan)} className="text-sm font-medium text-gray-700 dark:text-slate-300 cursor-pointer select-none">
                    Deseja escolher um plano?
                </label>
            </div>

            <div className="flex justify-between mt-4 border-t border-gray-100 dark:border-slate-700/50 pt-4">
                <button
                    onClick={onBack}
                    className="px-6 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                >
                    Voltar
                </button>
                <button
                    onClick={onFinish}
                    disabled={loading || ucs.length === 0 || isFormPartiallyFilled}
                    title={isFormPartiallyFilled ? "Clique em 'Adicionar esta UC' para incluir os dados preenchidos antes de continuar." : ""}
                    className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20 flex items-center gap-2 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed"
                >
                    {loading ? 'Processando...' : ((wantPlan || hasNextStep) ? <>Próximo <ArrowRight size={18} /></> : <><CheckCircle size={18} /> Finalizar Cadastro ({ucs.length})</>)}
                </button>
            </div>

        </div>
    );
};

export default StepUC;
